import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import OutputFormatPicker from '../shared/OutputFormatPicker';
import { useCompareUrls } from '../shared/heicDisplay';
import {
    IconCopy,
    IconCrop,
    IconDownload,
    IconFileTypeJpg,
    IconFileTypePng,
    IconPhoto,
    IconPhotoScan,
    IconRefresh,
    IconRotateClockwise,
    IconTrash,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { zipImageFiles } from '../../pdf/pdf-to-image/pdfToImageEngine';
import ImageCropDialog from '../../components/image/ImageCropDialog';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import useImagePaste from '../../components/image/useImagePaste';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ImageGridPreview from '../shared/ImageGridPreview';
import Hint from '../../../../../components/common/Hint';
import ToolCard from '../../components/common/ToolCard';
import ImagePreviewModal from '../../../../../components/common/ImagePreviewModal';
import {
    ASPECT_PRESETS,
    OUTPUT_FORMATS,
    cropImageFile,
    isSupportedCropImageFile,
    normaliseRotation,
    readImageDimensions,
    renderCropPreview,
    rotateCropPercent,
} from './cropImageEngine';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

const MAX_FILE_BYTES = 50 * 1024 * 1024;

const ACCEPT = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
    'image/bmp',
    'image/heic',
    'image/heif',
    'image/avif',
    '.jpg',
    '.jpeg',
    '.png',
    '.webp',
    '.gif',
    '.bmp',
    '.heic',
    '.heif',
    '.avif',
].join(',');

function segBtnClass(active) {
    return `flex flex-1 min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg border-none px-1 py-1.5 text-[11px] leading-tight transition-all disabled:cursor-not-allowed disabled:opacity-45 ${
        active
            ? 'bg-white font-semibold text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-amber-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

const FORMAT_ICONS = {
    jpg: IconFileTypeJpg,
    png: IconFileTypePng,
    webp: IconPhoto,
    avif: IconPhotoScan,
};

/**
 * Ratio glyph: a frame drawn at the preset's own proportions inside a fixed
 * box, so the shape itself says 16:9 before the label does. `free` gets a
 * dashed square — no ratio is being held.
 */
function AspectGlyph({ ratio, active }) {
    const color = active ? '#F59E0B' : '#9A9AA3';
    const BOX = 18;
    // `free` has no ratio: a plain square, drawn dashed below.
    const r = ratio || 1;
    const w = r >= 1 ? BOX : BOX * r;
    const h = r >= 1 ? BOX / r : BOX;
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <rect
                x={(24 - w) / 2}
                y={(24 - h) / 2}
                width={w}
                height={h}
                rx="2"
                fill="none"
                stroke={color}
                strokeWidth="1.7"
                strokeDasharray={ratio ? undefined : '3 2.5'}
            />
        </svg>
    );
}

const fileKey = (file) => `${file.name}-${file.size}`;

export default function CropImage() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [files, setFiles] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [cropModalIndex, setCropModalIndex] = useState(null);
    const [aspectPreset, setAspectPreset] = useState('free');
    const [format, setFormat] = useState('jpg');
    const [cropStates, setCropStates] = useState({});
    const [rotations, setRotations] = useState({});
    const [cropPreviews, setCropPreviews] = useState({});
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [results, setResults] = useState([]);
    const [resultPreviewIndex, setResultPreviewIndex] = useState(0);
    const [error, setError] = useState(null);

    const aspect = useMemo(
        () => ASPECT_PRESETS.find((p) => p.id === aspectPreset)?.value,
        [aspectPreset],
    );

    const activeFile = cropModalIndex != null ? files[cropModalIndex] ?? null : null;

    /**
     * Thumbnails of what each crop will actually produce.
     *
     * Showing the untouched picture with a "done" badge said nothing about the
     * region that was chosen, so every card whose crop (or rotation) is set gets
     * its selection rendered at thumbnail size.
     */
    const previewKey = useMemo(
        () => files
            .map((file, i) => {
                const crop = cropStates[i]?.cropPct;
                const rect = crop ? `${crop.x},${crop.y},${crop.width},${crop.height}` : '';
                return `${file.name}-${file.size}:${rect}:${rotations[i] || 0}`;
            })
            .join('|'),
        [files, cropStates, rotations],
    );

    useEffect(() => {
        let cancelled = false;
        const wanted = files
            .map((file, index) => ({ file, index }))
            .filter(({ index }) => cropStates[index]?.cropPct || rotations[index]);

        if (!wanted.length) {
            setCropPreviews((prev) => (Object.keys(prev).length ? {} : prev));
            return () => { cancelled = true; };
        }

        (async () => {
            const next = {};
            for (const { file, index } of wanted) {
                try {
                    // eslint-disable-next-line no-await-in-loop
                    next[index] = await renderCropPreview(
                        file,
                        cropStates[index]?.cropPct,
                        rotations[index] || 0,
                    );
                } catch { /* leave this card on the original */ }
            }
            if (!cancelled) setCropPreviews(next);
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewKey]);

    const handleCropStateChange = useCallback((index) => (state) => {
        setCropStates((prev) => {
            if (!state) {
                const next = { ...prev };
                delete next[index];
                return next;
            }
            return { ...prev, [index]: state };
        });
        setResults([]);
        setError(null);
    }, []);

    const addFiles = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        const images = list.filter(isSupportedCropImageFile);
        if (!images.length) {
            const msg = t('cropImage.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        let tooLarge = false;
        setFiles((prev) => {
            const next = [...prev];
            for (const file of images) {
                if (file.size > MAX_FILE_BYTES) {
                    tooLarge = true;
                    continue;
                }
                if (next.some((f) => fileKey(f) === fileKey(file))) continue;
                next.push(file);
            }
            return next;
        });

        if (tooLarge) {
            const msg = t('cropImage.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } else {
            setError(null);
        }
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied picture straight in.
    useImagePaste(addFiles);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    /** Re-key a per-index map after the file at `index` was removed. */
    const shiftAfterRemoval = (map, index) => {
        const next = {};
        Object.entries(map).forEach(([key, value]) => {
            const i = Number(key);
            if (i < index) next[i] = value;
            if (i > index) next[i - 1] = value;
        });
        return next;
    };

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
        setCropStates((prev) => shiftAfterRemoval(prev, index));
        setRotations((prev) => shiftAfterRemoval(prev, index));
        setActiveIndex((prev) => Math.max(0, prev >= index ? prev - 1 : prev));
        setCropModalIndex((prev) => {
            if (prev == null) return null;
            if (prev === index) return null;
            return prev > index ? prev - 1 : prev;
        });
        setResults([]);
        setError(null);
    };

    /**
     * Turn one image a quarter turn.
     *
     * The crop is measured on the turned picture, so the saved rectangle turns
     * with it — rotating an image you have already cropped keeps the selection
     * over the same part of the photo.
     */
    const rotateFile = (index) => {
        setRotations((prev) => ({ ...prev, [index]: normaliseRotation((prev[index] || 0) + 90) }));
        setCropStates((prev) => {
            const state = prev[index];
            if (!state?.cropPct) return prev;
            return { ...prev, [index]: { ...state, cropPct: rotateCropPercent(state.cropPct, 1) } };
        });
        setResults([]);
        setError(null);
    };

    /** A second copy of the same picture, with its crop and rotation. */
    const duplicateFile = (index) => {
        setFiles((prev) => {
            const file = prev[index];
            if (!file) return prev;
            const next = [...prev];
            next.splice(index + 1, 0, file);
            return next;
        });
        const shiftIn = (map) => {
            const next = {};
            Object.entries(map).forEach(([key, value]) => {
                const i = Number(key);
                if (i <= index) next[i] = value;
                else next[i + 1] = value;
            });
            if (map[index] !== undefined) next[index + 1] = map[index];
            return next;
        };
        setCropStates(shiftIn);
        setRotations(shiftIn);
        setResults([]);
        setError(null);
    };

    const handleReset = () => {
        setFiles([]);
        setCropStates({});
        setRotations({});
        setResults([]);
        setError(null);
        setProgress(null);
        setActiveIndex(0);
        setCropModalIndex(null);
        setAspectPreset('free');
        setFormat('jpg');
    };

    const invalidateResults = () => {
        setResults([]);
        setError(null);
        setProgress(null);
    };

    // Files with no result yet — adding pictures to a finished batch queues only
    // the new ones, so nothing already cropped is redone.
    const pendingIndexes = useMemo(
        () => files.reduce((acc, _file, i) => (results[i] ? acc : [...acc, i]), []),
        [files, results],
    );
    const allDone = files.length > 0 && pendingIndexes.length === 0;
    const canCrop = pendingIndexes.length > 0 && !error;

    const handleCrop = async () => {
        if (!canCrop || processing) return;

        recordToolUsage('image/crop', { category: 'image' });

        setProcessing(true);
        setError(null);
        const queue = pendingIndexes;

        try {
            const defaultCrop = (naturalWidth, naturalHeight) => {
                const aspectValue = aspect || null;
                let cropW;
                let cropH;

                if (aspectValue) {
                    cropW = Math.round(naturalWidth * 0.8);
                    cropH = Math.round(cropW / aspectValue);
                    const maxH = Math.round(naturalHeight * 0.8);
                    if (cropH > maxH) {
                        cropH = maxH;
                        cropW = Math.round(cropH * aspectValue);
                    }
                } else {
                    cropW = Math.round(naturalWidth * 0.8);
                    cropH = Math.round(naturalHeight * 0.8);
                }

                cropW = Math.max(1, Math.min(cropW, naturalWidth));
                cropH = Math.max(1, Math.min(cropH, naturalHeight));

                const x = Math.max(0, Math.round((naturalWidth - cropW) / 2));
                const y = Math.max(0, Math.round((naturalHeight - cropH) / 2));

                return { unit: 'px', x, y, width: cropW, height: cropH, __natural: true };
            };

            const cropPctToNatural = (cropPct, naturalWidth, naturalHeight) => {
                if (!cropPct || cropPct.unit !== '%' || !cropPct.width || !cropPct.height) return null;
                const x = Math.max(0, Math.round((cropPct.x / 100) * naturalWidth));
                const y = Math.max(0, Math.round((cropPct.y / 100) * naturalHeight));
                const width = Math.max(1, Math.round((cropPct.width / 100) * naturalWidth));
                const height = Math.max(1, Math.round((cropPct.height / 100) * naturalHeight));
                return { unit: 'px', x, y, width, height, __natural: true };
            };

            for (let k = 0; k < queue.length; k += 1) {
                const i = queue[k];
                const file = files[i];
                const state = cropStates[i];
                setProgress({ fileIndex: k + 1, fileTotal: queue.length });

                const raw = await readImageDimensions(file);
                // A quarter turn swaps the axes the crop percentages refer to.
                const swap = normaliseRotation(rotations[i] || 0) % 180 === 90;
                const dims = swap ? { width: raw.height, height: raw.width } : raw;
                const pixelCrop = state?.cropPct
                    ? cropPctToNatural(state.cropPct, dims.width, dims.height) || defaultCrop(dims.width, dims.height)
                    : defaultCrop(dims.width, dims.height);

                const result = await cropImageFile(file, null, pixelCrop, format, rotations[i] || 0);

                // Written at the file's own index (results line up with files) and
                // published immediately, so its card stops spinning right away.
                setResults((prev) => {
                    const next = [...prev];
                    next[i] = result;
                    return next;
                });
            }
            setResultPreviewIndex(queue[0] ?? 0);
            notifications.show({ title: t('common.success'), message: t('cropImage.done'), color: 'green' });
        } catch {
            const msg = t('cropImage.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const doneResults = useMemo(() => results.filter(Boolean), [results]);

    const handleDownload = async () => {
        if (!doneResults.length) return;
        if (doneResults.length === 1) {
            downloadBlob(doneResults[0].blob, doneResults[0].name);
            return;
        }
        const zip = await zipImageFiles(doneResults.map((r) => ({ name: r.name, blob: r.blob })));
        downloadBlob(zip, 'cropped-images.zip');
    };

    const status = deriveToolStatus({
        hasInput: files.length > 0,
        canRun: canCrop,
        processing,
        hasResult: doneResults.length > 0,
        hasError: !!error,
    });

    const successLabel = doneResults.length > 1
        ? `${t('cropImage.resultsTitle')} (${toBnDigits(doneResults.length)})`
        : t('cropImage.done');

    const processingLabel = progress?.fileTotal > 1
        ? t('cropImage.progressFileOnly', {
            file: toBnDigits(progress.fileIndex),
            files: toBnDigits(progress.fileTotal),
        })
        : t('cropImage.cropping');

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('cropImage.done'),
    });

    /** Per-card actions, mirroring what the other image tools offer. */
    const cardActions = useMemo(() => [
        {
            id: 'crop',
            icon: IconCrop,
            label: t('cropImage.actions.crop'),
            onClick: (index) => { setActiveIndex(index); setCropModalIndex(index); },
        },
        {
            id: 'rotate',
            icon: IconRotateClockwise,
            label: t('cropImage.actions.rotate'),
            onClick: rotateFile,
        },
        {
            id: 'duplicate',
            icon: IconCopy,
            label: t('common.duplicate'),
            onClick: duplicateFile,
        },
        {
            id: 'delete',
            icon: IconTrash,
            label: t('common.delete'),
            onClick: removeFile,
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [t]);

    const savedCropIndexes = useMemo(
        () => new Set(Object.keys(cropStates).map(Number)),
        [cropStates],
    );

    // ── Before/after lightbox ──
    // Object URLs live as long as the results do; the modal only ever gets URLs,
    // never blobs, so they are made once here instead of per render.
    const [lightboxIndex, setLightboxIndex] = useState(null);
    // HEIC originals are decoded for the "before" pane (see shared/heicDisplay).
    const compareUrls = useCompareUrls(results, files);

    const compareItems = useMemo(
        () => results
            .map((r, i) => (r ? {
                id: i,
                fileName: r.name,
                mimeType: r.blob.type || 'image/jpeg',
                aiImage: compareUrls[i]?.after,
                // What the shared modal calls the "before" pane.
                uploadedImage: compareUrls[i]?.before,
            } : null))
            .filter((item) => item?.aiImage),
        [results, compareUrls],
    );

    return (
        <>
        <ToolPageLayout
            slug="image/crop"
            subtitleKey="cropImage.subtitle"
            status={status}
            statusText={allDone && !processing ? successLabel : statusText}
            onReset={files.length || error ? handleReset : undefined}
            // Before a run the bar runs the tool; once every picture is done it downloads.
            primaryAction={allDone && !processing ? {
                label: doneResults.length > 1 ? t('cropImage.downloadZip') : t('cropImage.download'),
                onClick: handleDownload,
                icon: IconDownload,
            } : {
                label: t('cropImage.crop'),
                onClick: handleCrop,
                disabled: !canCrop || processing,
                loading: processing,
            }}
            // Drops the results only — the pictures and settings stay loaded, so another
            // run is one click away. Clearing everything is what রিসেট is for.
            secondaryAction={allDone && !processing ? {
                label: t('cropImage.startOver'),
                onClick: invalidateResults,
                icon: IconRefresh,
            } : undefined}
            showTrust={false}
            settings={(
                <div className="flex flex-col gap-3">
                    {/* Files are picked in the preview panel, so the left column
                        carries settings only — this input is what both the empty
                        state and the grid's "+" tile open. */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT}
                        multiple
                        className="hidden"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />

                    <ToolCard title={t('cropImage.aspectTitle')}>
                        <div className="flex gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                            {ASPECT_PRESETS.map((preset) => (
                                <Hint label={t(`cropImage.aspects.${preset.id}`)} key={preset.id}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setAspectPreset(preset.id);
                                        setCropStates({});
                                        invalidateResults();
                                    }}
                                    disabled={processing}
                                    className={segBtnClass(aspectPreset === preset.id)}
                                >
                                    <AspectGlyph ratio={preset.value} active={aspectPreset === preset.id} />
                                    {t(`cropImage.aspects.${preset.id}`)}
                                </button>
                                </Hint>
                            ))}
                        </div>
                    </ToolCard>

                    <OutputFormatPicker
                        value={format}
                        onChange={(next) => { setFormat(next); invalidateResults(); }}
                        formats={OUTPUT_FORMATS}
                        disabled={processing}
                        transparentOutput
                    />
                </div>
            )}
            preview={(
                <ToolPreviewPanel
                    // Fixed height suits one or two big pictures; a grid of three or more just hugs its rows.
                    tall={files.length <= 2}
                    title={t('cropImage.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('cropImage.previewEmpty')}
                    idleHint={t('cropImage.previewEmptyHint')}
                    processingLabel={processingLabel}
                    progress={progress?.fileTotal
                        ? Math.round((progress.fileIndex / progress.fileTotal) * 100)
                        : undefined}
                    error={error}
                    canvasTone="plain"
                    processingTone="none"
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showPasteHint
                    showIdle={!files.length && !error}
                >
                    {/* Kept mounted while processing: each card spins on its own, so
                        the wait shows the work and finished crops can be opened. */}
                    {files.length > 0 && !error ? (
                        <ImageGridPreview
                            // One picture fills the panel, two sit side by side, three or more make a grid.
                            adaptive
                            files={files}
                            results={results}
                            variant={doneResults.length ? 'crop' : 'preview'}
                            previews={doneResults.length ? {} : cropPreviews}
                            actions={processing || doneResults.length ? [] : cardActions}
                            busyIndexes={processing ? pendingIndexes : undefined}
                            // Before a run each card opens the crop dialog; after
                            // one it opens the before/after view of what came out.
                            onItemClick={doneResults.length
                                ? (index) => {
                                    if (!results[index]) return;
                                    setResultPreviewIndex(index);
                                    setLightboxIndex(index);
                                }
                                : (index) => {
                                    setActiveIndex(index);
                                    setCropModalIndex(index);
                                }}
                            activeIndex={doneResults.length ? resultPreviewIndex : activeIndex}
                            savedIndexes={doneResults.length ? undefined : savedCropIndexes}
                            savedLabel={t('common.done')}
                            onAddMore={processing ? undefined : () => fileInputRef.current?.click()}
                            onAddDrop={processing ? undefined : handleDrop}
                            addLabel={t('cropImage.addMore')}
                        />
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
        <ImageCropDialog
            file={activeFile}
            aspect={aspect}
            rotation={cropModalIndex != null ? rotations[cropModalIndex] || 0 : 0}
            savedCrop={cropModalIndex != null ? cropStates[cropModalIndex]?.cropPct : null}
            title={cropModalIndex != null ? t('toolPage.pageNumber', { number: toBnDigits(cropModalIndex + 1) }) : ''}
            onClose={() => setCropModalIndex(null)}
            onSave={(cropPct) => {
                if (cropModalIndex == null) return;
                handleCropStateChange(cropModalIndex)({ cropPct });
            }}
        />
        <ImagePreviewModal
            opened={lightboxIndex !== null}
            onClose={() => setLightboxIndex(null)}
            images={compareItems}
            initialImage={compareItems.find((item) => item.id === lightboxIndex) ?? null}
            // `adjusted` is the crop re-rendered with the viewer's
            // brightness/contrast/sharpness edits burnt in; without edits it is
            // null and the untouched result goes out.
            onDownloadImage={(img, adjusted) => {
                const r = results[img?.id];
                if (!r) return;
                downloadBlob(adjusted || r.blob, r.name);
            }}
            initialViewMode="side-by-side"
            useImageTitle
            hideEdit
            hidePrint
            hideShare
            hideCustomDownload
            hideDelete
        />
        </>
    );
}
